/**
 * View-preference persistence for the Virtual Library.
 *
 * Remembers whether the user last chose the Modern (SPA) or Classic (ASPX)
 * view, using localStorage so the choice survives page reloads and browser
 * restarts but is cleared when the user clears their browser data.
 *
 * The preference key is user-scoped: `vl_view_preference_<userId>` so that
 * different users on the same machine get independent preferences. The Classic
 * View (.NET) reads the same user-scoped key using the logged-in user’s id.
 *
 * Contract with the Classic View (.NET ASPX page):
 *   Before navigating to /VirtualLibrary, the ASPX page must run:
 *     localStorage.setItem(`vl_view_preference_${userId}`, 'modern');
 *
 * Fallback behaviour (first visit / cleared cache / private browsing):
 *   When no preference key is found, `indexPageLoader` treats the visit as
 *   Modern and persists 'modern' automatically. This means the SPA is
 *   self-sufficient: it works even before the Classic-side localStorage
 *   changes are deployed. Users who explicitly chose Classic will have
 *   `pref === 'classic'` and are redirected to /library/ by the loader.
 */

import bootConfig from 'utils/bootConfig';

const VIEW_PREF_KEY = `vl_view_preference_${bootConfig.authInfo.idLoggedInWebuser}`;
const CLASSIC_VIEW_URL = '/library/';

export type ViewPreference = 'modern' | 'classic';

/**
 * Read the stored view preference.
 * Returns null when localStorage is unavailable or the key has not been set.
 * The stored value is validated against the allowed set to guard against
 * tampered values.
 */
export function getViewPreference(): ViewPreference | null {
  try {
    const val = localStorage.getItem(VIEW_PREF_KEY);
    if (val === 'modern' || val === 'classic') return val;
    return null;
  } catch {
    // localStorage unavailable (private browsing, browser restrictions)
    return null;
  }
}

/**
 * Persist the user's view choice.
 * Safe to call immediately before a page navigation — the write completes
 * synchronously before the navigation begins.
 */
export function setViewPreference(pref: ViewPreference): void {
  try {
    localStorage.setItem(VIEW_PREF_KEY, pref);
  } catch {
    // Silently ignore — preference just won't persist this session
  }
}

/**
 * Save 'classic' and navigate to the Classic View.
 * Use this as the onClick handler for the "Classic View" button so the
 * choice is remembered before the SPA unloads.
 */
export function switchToClassic(): void {
  setViewPreference('classic');
  window.location.href = CLASSIC_VIEW_URL;
}
